import { useState } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { supabase } from '@/lib/supabaseClient';
import { formatStorageBytes, formatStorageMb } from '@/lib/getStorageUsage';

// ── Types ──────────────────────────────────────────────────────────────────────
export interface TenantStorageRow {
  id: string;
  name: string;
  slug: string;
  plan_code: string;
  status: string;
  storage_quota_mb: number;
  usedBytes: number;
}

interface Props {
  rows: TenantStorageRow[];
  onQuotaChange: (tenantId: string, newQuotaMb: number) => void;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const SW = { background: '#ffffff', color: '#0f172a', confirmButtonColor: '#2563eb' };

const TIERS = [
  { label: '512 MB', value: 512 },
  { label: '1 GB',   value: 1024 },
  { label: '5 GB',   value: 5120 },
  { label: '10 GB',  value: 10240 },
  { label: '50 GB',  value: 51200 },
  { label: '100 GB', value: 102400 },
  { label: '250 GB', value: 256000 },
  { label: '500 GB', value: 512000 },
];

// ── Helpers ────────────────────────────────────────────────────────────────────
function usagePct(usedBytes: number, quotaMb: number): number {
  if (!quotaMb) return 0;
  const usedMb = usedBytes / (1024 * 1024);
  return Math.min(100, Math.round((usedMb / quotaMb) * 100));
}

function statusLabel(pct: number): { label: string; cls: string; dot: string } {
  if (pct >= 90) return { label: 'Critical', cls: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-400' };
  if (pct >= 60) return { label: 'Warning',  cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-400' };
  return           { label: 'OK',       cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-400' };
}

function barColor(pct: number): string {
  if (pct >= 90) return 'bg-red-400';
  if (pct >= 60) return 'bg-amber-400';
  return 'bg-emerald-400';
}

// ── Edit Quota Swal ───────────────────────────────────────────────────────────
async function editQuota(tenant: TenantStorageRow, onQuotaChange: Props['onQuotaChange']) {
  const { value: raw } = await Swal.fire({
    title: `Storage Quota — ${tenant.name}`,
    html: `
      <p style="color:#64748b;font-size:12px;margin-bottom:10px">Actual: <strong>${formatStorageMb(tenant.storage_quota_mb)}</strong></p>
      <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:6px;margin-bottom:14px">
        ${TIERS.map((t) => `
          <button type="button"
            onclick="document.getElementById('swal2-input').value='${t.value}';document.querySelectorAll('[data-tier]').forEach(b=>b.style.background='#f8fafc');this.style.background='#dbeafe'"
            data-tier="${t.value}"
            style="padding:5px 4px;border:1px solid #e2e8f0;border-radius:8px;font-size:11px;cursor:pointer;background:${t.value === tenant.storage_quota_mb ? '#dbeafe' : '#f8fafc'}">
            ${t.label}
          </button>
        `).join('')}
      </div>
    `,
    input: 'number',
    inputLabel: 'Valor personalizado (MB)',
    inputValue: tenant.storage_quota_mb,
    inputAttributes: { min: '128', step: '128' },
    showCancelButton: true,
    confirmButtonText: 'Guardar',
    cancelButtonText: 'Cancelar',
    ...SW,
  });
  if (!raw) return;
  const newQuota = Number(raw);
  if (newQuota === tenant.storage_quota_mb || newQuota < 128) return;

  const { error } = await supabase
    .from('saas_tenants')
    .update({ storage_quota_mb: newQuota, updated_at: new Date().toISOString() })
    .eq('id', tenant.id);

  if (error) {
    Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW });
    return;
  }
  onQuotaChange(tenant.id, newQuota);
  Swal.fire({ title: 'Quota actualizada', icon: 'success', timer: 1200, showConfirmButton: false, ...SW });
}

// ── Component ─────────────────────────────────────────────────────────────────
/**
 * UsageTab shows per-tenant storage from usedBytes (derived from saas_tenants.storage_used_mb).
 * The VPS backend is the authoritative source — Supabase metadata (uploaded_files) is secondary.
 */
export default function UsageTab({ rows, onQuotaChange }: Props) {
  const [search, setSearch] = useState('');

  // ── Stats ──────────────────────────────────────────────────────────────────
  const totalQuotaMb  = rows.reduce((s, r) => s + r.storage_quota_mb, 0);
  const totalUsedBytes = rows.reduce((s, r) => s + r.usedBytes, 0);
  const totalUsedMb   = totalUsedBytes / (1024 * 1024);
  const overQuota     = rows.filter((r) => r.usedBytes / (1024 * 1024) > r.storage_quota_mb).length;
  const critical      = rows.filter((r) => usagePct(r.usedBytes, r.storage_quota_mb) >= 90).length;
  const platformPct   = totalQuotaMb ? Math.min(100, Math.round((totalUsedMb / totalQuotaMb) * 100)) : 0;

  // ── Filtered rows ──────────────────────────────────────────────────────────
  const filtered = rows.filter((r) =>
    !search || r.name.toLowerCase().includes(search.toLowerCase()) || r.slug.toLowerCase().includes(search.toLowerCase())
  );

  // ── Sort: highest usage % first ────────────────────────────────────────────
  const sorted = [...filtered].sort((a, b) => usagePct(b.usedBytes, b.storage_quota_mb) - usagePct(a.usedBytes, a.storage_quota_mb));

  return (
    <div className="space-y-6">

      {/* ── Stat cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Quota',   value: formatStorageMb(totalQuotaMb),  icon: 'ri-hard-drive-2-line', cls: 'bg-blue-50 text-blue-600' },
          { label: 'Total Used',    value: formatStorageBytes(totalUsedBytes), icon: 'ri-database-2-line', cls: 'bg-violet-50 text-violet-600' },
          { label: 'Available',     value: formatStorageMb(Math.max(0, totalQuotaMb - totalUsedMb)), icon: 'ri-inbox-unarchive-line', cls: 'bg-emerald-50 text-emerald-600' },
          { label: 'Over Quota',    value: overQuota, icon: 'ri-error-warning-line', cls: overQuota > 0 ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-400' },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
            <span className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${s.cls.split(' ')[0]}`}>
              <i className={`${s.icon} text-base ${s.cls.split(' ')[1]}`}></i>
            </span>
            <div className="min-w-0">
              <p className="text-slate-400 text-xs uppercase tracking-wider truncate">{s.label}</p>
              <p className="text-slate-900 text-xl font-bold">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Platform bar ── */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-slate-700 text-sm font-semibold">Platform Usage</p>
            <p className="text-slate-400 text-xs mt-0.5">Source: <span className="font-mono">saas_tenants.storage_used_mb</span> (VPS authoritative)</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-400">
            {critical > 0 && (
              <span className="flex items-center gap-1.5 text-red-600 font-medium">
                <i className="ri-error-warning-fill text-xs"></i>
                {critical} critical
              </span>
            )}
            <span>{rows.length} tenants</span>
          </div>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden mb-2">
          <div className={`h-2.5 rounded-full transition-all ${barColor(platformPct)}`} style={{ width: `${platformPct}%` }} />
        </div>
        <div className="flex justify-between text-xs text-slate-400">
          <span>{formatStorageMb(totalUsedMb)} used</span>
          <span className="font-semibold text-slate-600">{platformPct}%</span>
          <span>{formatStorageMb(totalQuotaMb)} allocated</span>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {/* Toolbar */}
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="text"
              placeholder="Buscar tenant..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 placeholder:text-slate-400"
            />
          </div>
          <p className="text-slate-400 text-xs whitespace-nowrap">{sorted.length} de {rows.length} tenants</p>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Tenant</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Usado</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Quota</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-40">Uso %</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Estado</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-slate-400 text-sm">
                    <i className="ri-hard-drive-line text-2xl mb-2 block text-slate-200"></i>
                    Sin tenants
                  </td>
                </tr>
              ) : (
                sorted.map((row) => {
                  const pct = usagePct(row.usedBytes, row.storage_quota_mb);
                  const st  = statusLabel(pct);
                  return (
                    <tr key={row.id} className="hover:bg-slate-50/60 transition-colors">
                      {/* Tenant */}
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-100 text-blue-700 text-xs font-bold shrink-0">
                            {row.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-slate-800 font-medium text-sm truncate">{row.name}</p>
                            <p className="text-slate-400 text-xs truncate">/{row.slug}</p>
                          </div>
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-500 uppercase tracking-wide shrink-0">
                            {row.plan_code}
                          </span>
                        </div>
                      </td>

                      {/* Used */}
                      <td className="px-4 py-3.5 text-right">
                        <span className="text-slate-700 font-semibold text-sm">{formatStorageBytes(row.usedBytes)}</span>
                      </td>

                      {/* Quota */}
                      <td className="px-4 py-3.5 text-right">
                        <span className="text-slate-400 text-sm">{formatStorageMb(row.storage_quota_mb)}</span>
                      </td>

                      {/* Bar */}
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden min-w-[60px]">
                            <div
                              className={`h-1.5 rounded-full transition-all ${barColor(pct)}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className={`text-xs font-bold w-9 text-right shrink-0 ${
                            pct >= 90 ? 'text-red-600' : pct >= 60 ? 'text-amber-600' : 'text-slate-400'
                          }`}>
                            {pct}%
                          </span>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${st.cls}`}>
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${st.dot}`}></span>
                          {st.label}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3.5">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => editQuota(row, onQuotaChange)}
                            title="Editar quota"
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
                          >
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <Link
                            to={`/sa/tenants/${row.id}`}
                            title="Ver tenant"
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                          >
                            <i className="ri-external-link-line text-sm"></i>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
